import { ForgePress } from "@/components/site/DecorativeBG";

const IN_HOUSE = [
  {
    name: "Forging Shop",
    body: "300 MT per month installed capacity, along with MPI testing machine.",
  },
  {
    name: "Machine Shop",
    body: "Highly precise CNC turning centres — 36 nos, turning capacity up to ⌀1000 mm · VMC with 4th-axis — 20 nos · VTL — max ⌀900 mm.",
  },
  {
    name: "Welding Shop",
    body: "All types of ferrous and non-ferrous welding, with WPS and WPQR qualification.",
  },
  {
    name: "Silver Plating",
    body: "Cu and silver plating facility, along with XRF Fischer gauge.",
  },
  {
    name: "Paint Shop",
    body: "Liquid spray painting with shot-blasting and baking oven.",
  },
];

const PARTNER_FACILITIES = [
  { name: "Low Pressure Die Casting", body: "Pressure-controlled aluminium castings for engineering solutions." },
  { name: "Gravity Die Casting", body: "Aluminium / non-ferrous gravity castings with full machining." },
  { name: "High Pressure Die Casting", body: "Thin-walled aluminium parts at scale." },
  { name: "Rubber Moulding", body: "Compression / injection-moulded rubber components for sealing & vibration." },
  {
    name: "Investment Die Casting",
    body: "Highly precise investment die casting for Copper, Aluminium and Stainless Steel.",
  },
];

function ImageCard({ src, alt, srcWebp }) {
  return (
    <div className="bg-white border border-gray-200 p-3 sm:p-5 lg:p-8 shadow-sm">
      <picture>
        {srcWebp && <source srcSet={srcWebp} type="image/webp" />}
        <img
          src={src}
          alt={alt}
          className="w-full h-auto"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </picture>
    </div>
  );
}

export default function Facilities() {
  return (
    <section
      id="facilities"
      data-testid="facilities-section"
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      <ForgePress className="top-10 right-2 lg:right-12 hidden md:block" opacity={0.22} bounce />
      <div className="container-x relative">
        <div className="flex items-end justify-between flex-wrap gap-6 max-w-5xl mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow">Facilities</span>
            </div>
            <h2 className="h-section">
              In-house power,
              <br />
              <span className="text-brand-blue">extended by an audited partner network.</span>
            </h2>
          </div>
          <p className="text-brand-text/70 max-w-md leading-relaxed">
            Tirupati Global Sourcing Company (TGSC) combines our own group
            production shops with a network of audited Indian manufacturing
            partners — covering the full breadth of precision component
            manufacturing under one roof.
          </p>
        </div>

        {/* 1. Product Capability — moved from Products to Facilities */}
        <div>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="divider-rule" />
                <span className="eyebrow">Product Capability</span>
              </div>
              <h3 className="font-heading font-extrabold text-2xl lg:text-3xl text-brand-navy tracking-tight">
                Casting · Machining · Forging · Moulding · Finishing.
              </h3>
            </div>
            <p className="text-brand-text/65 text-sm max-w-md leading-relaxed">
              A complete capability matrix — from raw material to finished,
              inspected and packaged components.
            </p>
          </div>

          <ImageCard
            src="/capabilities.png"
            srcWebp="/capabilities.webp"
            alt="Our Capabilities — Gravity, Low & High Pressure Die Castings, CNC Machining, Forged Parts, Rubber Moulding, Fabrication, Surface Treatment"
          />
        </div>

        {/* 2. In-House Process */}
        <div className="mt-20 border-t border-gray-200 pt-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="divider-rule" />
                <span className="eyebrow">In-House Processes</span>
              </div>
              <h3
                data-testid="inhouse-title"
                className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-[1.02]"
              >
                In-House <span className="text-brand-blue">Process</span>
              </h3>
            </div>
            <p className="text-brand-text/65 text-sm max-w-md leading-relaxed">
              Production shops operated directly by our group companies at our
              Pune facilities.
            </p>
          </div>

          <ImageCard
            src="/inhouse-shops-v3.jpg"
            alt="In-House Process — Forging, Machine, Welding, Paint and Silver Plating Shops"
          />

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
            {IN_HOUSE.map((p, i) => (
              <div
                key={p.name}
                data-testid={`inhouse-${i}`}
                className="bg-white p-5 hover:bg-brand-soft transition-colors"
              >
                <span className="font-heading font-extrabold text-brand-orange text-xs tracking-[0.15em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="mt-2 font-heading font-extrabold text-brand-navy">
                  {p.name}
                </div>
                <div className="mt-2 text-xs text-brand-text/65 leading-snug">
                  {p.body}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 3. Partner Process */}
        <div className="mt-20 border-t border-gray-200 pt-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="divider-rule" />
                <span className="eyebrow">Approved Partner&apos;s Facilities</span>
              </div>
              <h3
                data-testid="partner-title"
                className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-[1.02]"
              >
                Partner&apos;s <span className="text-brand-blue">Process</span>
              </h3>
            </div>
            <p className="text-brand-text/65 text-sm max-w-md leading-relaxed">
              Processes routed to pre-audited Indian manufacturing partners
              under TGSC&apos;s quality control plan, supplier development
              programme and consolidated logistics.
            </p>
          </div>

          <ImageCard
            src="/partner-facilities.png"
            srcWebp="/partner-facilities.webp"
            alt="Approved Partner Facilities"
          />

          {/* Featured: Investment Die Casting — new partner process */}
          <div
            data-testid="partner-investment-die-casting"
            className="mt-8 grid lg:grid-cols-12 gap-6 lg:gap-8 items-stretch bg-white border border-gray-200"
          >
            <div className="lg:col-span-7 p-2">
              <img
                src="/investment-die-casting.jpg"
                alt="Investment Die Casting facility"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-5 p-6 lg:p-8 flex flex-col justify-center">
              <span className="font-heading font-extrabold text-brand-orange text-xs tracking-[0.18em] uppercase">
                New Partner Process
              </span>
              <h4 className="mt-3 font-heading font-extrabold text-2xl lg:text-3xl text-brand-navy leading-tight">
                Investment Die Casting
              </h4>
              <p className="mt-4 text-sm lg:text-base text-brand-text/75 leading-relaxed">
                Highly precise investment die casting for{" "}
                <span className="font-semibold text-brand-navy">
                  Copper, Aluminium and Stainless Steel
                </span>{" "}
                — delivering tight-tolerance, near-net-shape components for
                demanding OEM applications.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
            {PARTNER_FACILITIES.map((p, i) => (
              <div
                key={p.name}
                data-testid={`partner-${i}`}
                className="bg-white p-5 hover:bg-brand-soft transition-colors"
              >
                <span className="font-heading font-extrabold text-brand-orange text-xs tracking-[0.15em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="mt-2 font-heading font-extrabold text-brand-navy">
                  {p.name}
                </div>
                <div className="mt-2 text-xs text-brand-text/65 leading-snug">
                  {p.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
